import * as React from 'react';
import { Navigate } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import apiClient, { getErrorMessage, shouldRedirectToLogin } from '../services/api-client';
import AuthService from '../services/auth';
import CustomSnackbar from '../components/CustomSnackbar';

const ALL_MODULES = [
  { key: 'visits', label: 'Gestión de Visitas', icon: '👥' },
  { key: 'amenities', label: 'Amenidades', icon: '🏊' },
  { key: 'issues', label: 'Reportes de Averías', icon: '🔧' },
  { key: 'parksharing', label: 'Parqueos Compartidos', icon: '🅿️' },
  { key: 'reporting', label: 'Reportería', icon: '📊' },
  { key: 'directory', label: 'Directorio', icon: '📋' },
  { key: 'airbnb', label: 'Airbnb', icon: '🏠' },
];

const NOTIFICATION_MODES = [
  { value: 'mail', label: 'Email' },
  { value: 'push', label: 'Push Notification' },
  { value: 'both', label: 'Ambos' },
];

const UNIT_TYPES = [
  { value: 'Casa', label: 'Casa' },
  { value: 'Apartamento', label: 'Apartamento' },
];

const TABS = [
  { label: 'Contacto y Notificaciones', icon: <NotificationsOutlinedIcon fontSize="small" /> },
  { label: 'Módulos', icon: <ViewModuleOutlinedIcon fontSize="small" /> },
  { label: 'Configuración Adicional', icon: <TuneOutlinedIcon fontSize="small" /> },
];

const DEFAULT_AIRBNB_CONFIG = {
  id_number: {
    ask: false,
    required: false,
  },
  id_picture: {
    ask: true,
    required: true,
  },
  languages: ['es', 'en', 'pt'],
};

const DEFAULT_RULESET = {
  es: {
    title: 'Reglamento para Huéspedes',
    rules: [
      'Está prohibido generar ruidos o sonidos molestos, como televisores o equipos de sonido con volumen alto, que puedan molestar a otros ocupantes del edificio. Estos dispositivos deben mantenerse a un nivel moderado.',
      'Se debe respetar la tranquilidad y el orden dentro de las instalaciones, por lo que no se permite la realización de fiestas ni eventos sociales en ninguna parte de la propiedad.',
      'Las amenidades del edificio requieren reservación previa, por favor consultar en recepción para más información de cómo utilizarlas.',
      'Es prohibido fumar en las instalaciones del edificio, adentro de los apartamentos y en las áreas comunes.',
      'No se permite el ingreso de huéspedes externos a la reservación de Airbnb.',
      'En caso de no cumplir con las reglas, la administración tendrá la obligación de advertirte, y si no se acatan las advertencias, tendrá la potestad de dar por terminada la estancia y solicitar que abandones las instalaciones.',
    ],
  },
};

const DEFAULT_VISIT_RULES = {
  default: {
    vehicle: true,
    walking: true,
    default_lifespan_hours: 24,
    allow_multiple_entries: true,
    allow_longer_lifespan: true,
    max_lifespan_hours: 168,
  },
};

function buildNormalizedAirbnbConfig(config) {
  const nextConfig = JSON.parse(JSON.stringify(DEFAULT_AIRBNB_CONFIG));
  if (config && typeof config === 'object') {
    nextConfig.id_number.ask = Boolean(config.id_number?.ask);
    nextConfig.id_number.required = Boolean(config.id_number?.required);
    nextConfig.id_picture.ask = config.id_picture?.ask !== undefined ? Boolean(config.id_picture.ask) : true;
    nextConfig.id_picture.required = config.id_picture?.required !== undefined ? Boolean(config.id_picture.required) : true;
    if (Array.isArray(config.languages)) {
      const validLanguages = config.languages.filter((language, index) => ['es', 'en', 'pt'].includes(language) && config.languages.indexOf(language) === index);
      nextConfig.languages = ['es', 'en', ...(validLanguages.includes('pt') ? ['pt'] : [])];
    }
  }
  if (!nextConfig.id_number.ask) {
    nextConfig.id_number.required = false;
  }
  if (!nextConfig.id_picture.ask) {
    nextConfig.id_picture.required = false;
  }
  return nextConfig;
}

function buildNormalizedRuleset(ruleset, airbnbConfig) {
  const normalizedRuleset = JSON.parse(JSON.stringify(DEFAULT_RULESET));
  if (ruleset && typeof ruleset === 'object') {
    Object.entries(ruleset).forEach(([language, block]) => {
      if (!block || typeof block !== 'object') {
        return;
      }
      normalizedRuleset[language] = {
        title: String(block.title || '').trim(),
        rules: Array.isArray(block.rules) ? block.rules.map((rule) => String(rule).trim()).filter(Boolean) : [],
      };
    });
  }
  if (!normalizedRuleset.es?.title) {
    normalizedRuleset.es = JSON.parse(JSON.stringify(DEFAULT_RULESET.es));
  }
  if (!Array.isArray(normalizedRuleset.es?.rules) || normalizedRuleset.es.rules.length === 0) {
    normalizedRuleset.es = JSON.parse(JSON.stringify(DEFAULT_RULESET.es));
  }
  Object.keys(normalizedRuleset).forEach((language) => {
    if (language === 'es') {
      return;
    }
    const block = normalizedRuleset[language];
    if (!block?.title || !Array.isArray(block.rules) || block.rules.length === 0) {
      delete normalizedRuleset[language];
    }
  });
  if (!airbnbConfig?.languages?.includes('pt')) {
    delete normalizedRuleset.pt;
  }
  return normalizedRuleset;
}

function buildNormalizedVisitRules(rules) {
  const normalizedRules = JSON.parse(JSON.stringify(DEFAULT_VISIT_RULES));
  const source = rules?.default && typeof rules.default === 'object' ? rules.default : {};
  const defaultLifespanHours = Number(source.default_lifespan_hours);
  const maxLifespanHours = Number(source.max_lifespan_hours);
  normalizedRules.default.vehicle = source.vehicle !== undefined ? Boolean(source.vehicle) : true;
  normalizedRules.default.walking = source.walking !== undefined ? Boolean(source.walking) : true;
  normalizedRules.default.default_lifespan_hours = Number.isFinite(defaultLifespanHours) && defaultLifespanHours > 0 ? Math.round(defaultLifespanHours) : 24;
  normalizedRules.default.allow_multiple_entries = source.allow_multiple_entries !== undefined ? Boolean(source.allow_multiple_entries) : true;
  normalizedRules.default.allow_longer_lifespan = source.allow_longer_lifespan !== undefined ? Boolean(source.allow_longer_lifespan) : true;
  normalizedRules.default.max_lifespan_hours = Number.isFinite(maxLifespanHours) && maxLifespanHours > 0 ? Math.round(maxLifespanHours) : 168;
  if (normalizedRules.default.max_lifespan_hours < normalizedRules.default.default_lifespan_hours) {
    normalizedRules.default.max_lifespan_hours = normalizedRules.default.default_lifespan_hours;
  }
  return normalizedRules;
}

function normalizeDashboardConfig(config) {
  if (!config) {
    return config;
  }
  const nextConfig = JSON.parse(JSON.stringify(config));
  nextConfig.airbnb_config = buildNormalizedAirbnbConfig(config.airbnb_config);
  nextConfig.ruleset = buildNormalizedRuleset(config.ruleset, nextConfig.airbnb_config);
  nextConfig.rules = buildNormalizedVisitRules(config.rules);
  return nextConfig;
}

function RulesEditor({ title, value, onChange, onAddRule, onRemoveRule }) {
  return (
    <Stack spacing={2}>
      <TextField
        label="Título"
        value={value?.title || ''}
        onChange={(event) => onChange('title', event.target.value)}
        fullWidth
      />
      <Stack spacing={1.5}>
        {(value?.rules || []).map((rule, index) => (
          <Stack key={`rule-${index}`} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
            <TextField
              label={`Regla ${index + 1}`}
              value={rule}
              onChange={(event) => onChange(`rules.${index}`, event.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <Button color="error" onClick={() => onRemoveRule(index)} sx={{ minWidth: { sm: 120 } }}>
              Eliminar
            </Button>
          </Stack>
        ))}
        <Button variant="outlined" color="secondary" onClick={onAddRule} sx={{ alignSelf: 'flex-start' }}>
          Agregar regla
        </Button>
      </Stack>
    </Stack>
  );
}

function MailingListField({ label, list, onAdd, onRemove }) {
  const [input, setInput] = React.useState('');

  const handleAdd = React.useCallback(() => {
    const value = input.trim();
    if (!value || !value.includes('@')) {
      return;
    }
    onAdd(value);
    setInput('');
  }, [input, onAdd]);

  return (
    <Stack spacing={1.5}>
      <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary' }}>{label}</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <TextField
          fullWidth
          size="small"
          type="email"
          value={input}
          label="Correo"
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              handleAdd();
            }
          }}
        />
        <Button variant="contained" color="secondary" onClick={handleAdd} sx={{ minWidth: { sm: 120 } }}>
          Agregar
        </Button>
      </Stack>
      {list.length > 0 && (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {list.map((email, index) => (
            <Chip key={`${email}-${index}`} label={email} onDelete={() => onRemove(index)} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

export default function AdminDashboard({ setNavbarState, navbarState }) {
  const navigate = useNavigate();
  const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' });
  const [loading, setLoading] = React.useState(true);
  const [activeTab, setActiveTab] = React.useState(0);
  const [config, setConfig] = React.useState(null);
  const [error, setError] = React.useState('');
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [snackbarSeverity, setSnackbarSeverity] = React.useState('success');
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const [settingsScreen, setSettingsScreen] = React.useState(null);
  const [rulesetBusy, setRulesetBusy] = React.useState(false);
  const [planCatalog, setPlanCatalog] = React.useState([]);
  const orgKey = AuthService.getCurrentUserOrgKey();
  const saveTimeoutRef = React.useRef(null);

  const handleOpenAirbnbSettings = React.useCallback(() => {
    setActiveTab(1);
    setSettingsScreen('airbnb');
  }, []);

  const handleCloseAirbnbSettings = React.useCallback(() => {
    setSettingsScreen(null);
    setActiveTab(1);
  }, []);

  const handleOpenVisitSettings = React.useCallback(() => {
    setActiveTab(1);
    setSettingsScreen('visits');
  }, []);

  const handleCloseVisitSettings = React.useCallback(() => {
    setSettingsScreen(null);
    setActiveTab(1);
  }, []);

  const fetchPlanCatalog = React.useCallback(async () => {
    try {
      const response = await apiClient.get('plans', { parseAppConfig: false }, { action: 'fetch_plan_catalog' });
      if (response.status === 200 && Array.isArray(response.data?.data?.plans)) {
        setPlanCatalog(response.data.data.plans);
      } else {
        setPlanCatalog([]);
      }
    } catch (err) {
      console.error('Failed to load plan catalog', err);
      setPlanCatalog([]);
    }
  }, []);

  const fetchConfig = React.useCallback(async () => {
    if (!orgKey) {
      setLoading(false);
      setError('No se encontró la organización del administrador.');
      return;
    }
    try {
      const response = await apiClient.get(`landing/dashboard/${orgKey}`, {}, { action: 'fetch_admin_dashboard_config' });
      if (response.status === 200) {
        setConfig(normalizeDashboardConfig(response.data.data));
        setError('');
      }
    } catch (fetchError) {
      if (shouldRedirectToLogin(fetchError)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      setError(getErrorMessage(fetchError, 'No se pudo cargar la configuración.'));
    } finally {
      setLoading(false);
    }
  }, [orgKey]);

  React.useEffect(() => {
    setNavbarState({ ...navbarState, showBack: true, showOptions: false, showSearch: false });
    fetchPlanCatalog();
    fetchConfig();
  }, [fetchConfig, fetchPlanCatalog]);

  const updateConfig = React.useCallback((path, value) => {
    setConfig((prevConfig) => {
      if (!prevConfig) {
        return prevConfig;
      }
      const keys = path.split('.');
      const updated = JSON.parse(JSON.stringify(prevConfig));
      let current = updated;
      for (let index = 0; index < keys.length - 1; index += 1) {
        current[keys[index]] = { ...(current[keys[index]] || {}) };
        current = current[keys[index]];
      }
      current[keys[keys.length - 1]] = value;
      updated.airbnb_config = buildNormalizedAirbnbConfig(updated.airbnb_config);
      updated.ruleset = buildNormalizedRuleset(updated.ruleset, updated.airbnb_config);
      updated.rules = buildNormalizedVisitRules(updated.rules);
      return updated;
    });
    setHasUnsavedChanges(true);
  }, []);

  const updateRuleset = React.useCallback((language, path, value) => {
    setConfig((prevConfig) => {
      if (!prevConfig) {
        return prevConfig;
      }
      const updated = JSON.parse(JSON.stringify(prevConfig));
      if (!updated.ruleset?.[language]) {
        updated.ruleset = updated.ruleset || {};
        updated.ruleset[language] = { title: '', rules: [] };
      }
      const keys = path.split('.');
      let current = updated.ruleset[language];
      for (let index = 0; index < keys.length - 1; index += 1) {
        const key = keys[index];
        if (Array.isArray(current)) {
          current = current[Number(key)];
        } else {
          current[key] = Array.isArray(current[key]) ? [...current[key]] : { ...(current[key] || {}) };
          current = current[key];
        }
      }
      current[keys[keys.length - 1]] = value;
      updated.ruleset = buildNormalizedRuleset(updated.ruleset, updated.airbnb_config);
      return updated;
    });
    setHasUnsavedChanges(true);
  }, []);

  const selectedPlan = React.useMemo(() => {
    if (!config) {
      return null;
    }
    const planId = (config.subscription && config.subscription.plan) || config.plan;
    if (!planId) {
      return null;
    }
    return planCatalog.find((plan) => plan.id === planId) || null;
  }, [config, planCatalog]);

  const planChipLabel = React.useMemo(() => {
    if (selectedPlan) {
      const cycle = selectedPlan.billing_cycle || 'mes';
      return `${selectedPlan.name} (${selectedPlan.currency}${selectedPlan.price}/${cycle})`;
    }
    if (config?.plan) {
      return `Plan ${String(config.plan).toUpperCase()}`;
    }
    return null;
  }, [config?.plan, selectedPlan]);

  const addAirbnbRule = React.useCallback((language) => {
    setConfig((prevConfig) => {
      if (!prevConfig) {
        return prevConfig;
      }
      const updated = JSON.parse(JSON.stringify(prevConfig));
      const currentRules = Array.isArray(updated.ruleset?.[language]?.rules) ? [...updated.ruleset[language].rules] : [];
      currentRules.push('');
      updated.ruleset = updated.ruleset || {};
      updated.ruleset[language] = {
        title: updated.ruleset?.[language]?.title || '',
        rules: currentRules,
      };
      return updated;
    });
    setHasUnsavedChanges(true);
  }, []);

  const removeAirbnbRule = React.useCallback((language, index) => {
    setConfig((prevConfig) => {
      if (!prevConfig) {
        return prevConfig;
      }
      const updated = JSON.parse(JSON.stringify(prevConfig));
      const currentRules = Array.isArray(updated.ruleset?.[language]?.rules) ? [...updated.ruleset[language].rules] : [];
      currentRules.splice(index, 1);
      updated.ruleset[language] = {
        title: updated.ruleset?.[language]?.title || '',
        rules: currentRules,
      };
      updated.ruleset = buildNormalizedRuleset(updated.ruleset, updated.airbnb_config);
      return updated;
    });
    setHasUnsavedChanges(true);
  }, []);

  const setPortugueseEnabled = React.useCallback((enabled) => {
    setConfig((prevConfig) => {
      if (!prevConfig) {
        return prevConfig;
      }
      const updated = JSON.parse(JSON.stringify(prevConfig));
      updated.airbnb_config = buildNormalizedAirbnbConfig({
        ...updated.airbnb_config,
        languages: enabled ? ['es', 'en', 'pt'] : ['es', 'en'],
      });
      updated.ruleset = buildNormalizedRuleset(updated.ruleset, updated.airbnb_config);
      return updated;
    });
    setHasUnsavedChanges(true);
  }, []);

  const generateRuleset = React.useCallback(async (mode) => {
    if (!config || !orgKey) {
      return;
    }
    setRulesetBusy(true);
    try {
      const response = await apiClient.post(
        `landing/dashboard/${orgKey}/airbnb-ruleset/generate`,
        {
          mode,
          languages: config.airbnb_config?.languages?.includes('pt') ? ['en', 'pt'] : ['en'],
          airbnb_config: config.airbnb_config,
          ruleset: config.ruleset,
        },
        {},
        { action: 'generate_airbnb_ruleset' }
      );
      if (response.status === 200) {
        setConfig((prevConfig) => normalizeDashboardConfig({ ...prevConfig, ruleset: response.data.data }));
        setHasUnsavedChanges(true);
        setSnackbarSeverity('success');
        setSnackbarMessage(mode === 'missing' ? 'Idiomas faltantes generados.' : 'Idiomas regenerados correctamente.');
        setSnackbarOpen(true);
      }
    } catch (generationError) {
      if (shouldRedirectToLogin(generationError)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      setSnackbarSeverity('error');
      setSnackbarMessage(getErrorMessage(generationError, 'No se pudieron generar los idiomas.'));
      setSnackbarOpen(true);
    } finally {
      setRulesetBusy(false);
    }
  }, [config, orgKey]);

  const addToList = React.useCallback((field, value) => {
    setConfig((prevConfig) => {
      if (!prevConfig) {
        return prevConfig;
      }
      const currentList = Array.isArray(prevConfig[field]) ? [...prevConfig[field]] : [];
      if (currentList.includes(value)) {
        return prevConfig;
      }
      return { ...prevConfig, [field]: [...currentList, value] };
    });
    setHasUnsavedChanges(true);
  }, []);

  const removeFromList = React.useCallback((field, index) => {
    setConfig((prevConfig) => {
      if (!prevConfig) {
        return prevConfig;
      }
      const currentList = Array.isArray(prevConfig[field]) ? [...prevConfig[field]] : [];
      currentList.splice(index, 1);
      return { ...prevConfig, [field]: currentList };
    });
    setHasUnsavedChanges(true);
  }, []);

  const performSave = React.useCallback(async (configToSave) => {
    if (!configToSave || !orgKey) {
      return;
    }
    try {
      const response = await apiClient.put(`landing/dashboard/${orgKey}`, configToSave, {}, { action: 'save_admin_dashboard_config' });
      if (response.status === 200) {
        setConfig(normalizeDashboardConfig(response.data.data || configToSave));
        setSnackbarSeverity('success');
        setSnackbarMessage('Configuración guardada exitosamente.');
        setSnackbarOpen(true);
        setHasUnsavedChanges(false);
      }
    } catch (saveError) {
      if (shouldRedirectToLogin(saveError)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      setSnackbarSeverity('error');
      setSnackbarMessage(getErrorMessage(saveError, 'No se pudo guardar la configuración.'));
      setSnackbarOpen(true);
    }
  }, [orgKey]);

  React.useEffect(() => {
    if (hasUnsavedChanges && config) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        performSave(config);
      }, 500);
    }
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [config, hasUnsavedChanges, performSave]);

  if (!AuthService.userRoleIncludes(['admin'])) {
    return <Navigate to="/" replace={true} />;
  }

  if (redirect.trigger) {
    return <Navigate to={redirect.route} replace={true} />;
  }

  const airbnbSettingsContent = config && (
    <Card sx={{ borderRadius: '16px', boxShadow: 0, border: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}>
      <CardContent sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 900, color: 'text.primary' }}>Configuración de Airbnb</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', mt: 0.5 }}>
              Administra los datos requeridos del formulario y el reglamento usado para aceptación de huéspedes.
            </Typography>
          </Box>

          <Stack spacing={1}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary' }}>Documento de identificación</Typography>
            <FormControlLabel
              control={<Switch checked={Boolean(config.airbnb_config?.id_number?.ask)} onChange={(event) => updateConfig('airbnb_config.id_number.ask', event.target.checked)} color="secondary" />}
              label="Solicitar número de documento"
            />
            <FormControlLabel
              control={<Switch checked={Boolean(config.airbnb_config?.id_number?.required)} onChange={(event) => updateConfig('airbnb_config.id_number.required', event.target.checked)} color="secondary" disabled={!config.airbnb_config?.id_number?.ask} />}
              label="Número de documento obligatorio"
            />
          </Stack>

          <Stack spacing={1}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary' }}>Imagen del documento</Typography>
            <FormControlLabel
              control={<Switch checked={Boolean(config.airbnb_config?.id_picture?.ask)} onChange={(event) => updateConfig('airbnb_config.id_picture.ask', event.target.checked)} color="secondary" />}
              label="Solicitar imagen del documento"
            />
            <FormControlLabel
              control={<Switch checked={Boolean(config.airbnb_config?.id_picture?.required)} onChange={(event) => updateConfig('airbnb_config.id_picture.required', event.target.checked)} color="secondary" disabled={!config.airbnb_config?.id_picture?.ask} />}
              label="Imagen del documento obligatoria"
            />
          </Stack>

          <Stack spacing={1}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary' }}>Idiomas activos</Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip label="Español" color="secondary" variant="filled" />
              <Chip label="English" color="secondary" variant="outlined" />
            </Stack>
            <FormControlLabel
              control={<Switch checked={Boolean(config.airbnb_config?.languages?.includes('pt'))} onChange={(event) => setPortugueseEnabled(event.target.checked)} color="secondary" />}
              label="Habilitar portugués"
            />
          </Stack>

          <Divider />

          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ md: 'center' }}>
              <Box>
                <Typography sx={{ fontSize: 16, fontWeight: 900, color: 'text.primary' }}>Ruleset para huéspedes</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', mt: 0.5 }}>
                  Español es la fuente de verdad. Desde aquí puedes generar inglés y, si está habilitado, portugués.
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button variant="outlined" color="secondary" onClick={() => generateRuleset('missing')} disabled={rulesetBusy}>
                  {rulesetBusy ? 'Generando...' : 'Generar faltantes'}
                </Button>
                <Button variant="contained" color="secondary" onClick={() => generateRuleset('regenerate')} disabled={rulesetBusy}>
                  {rulesetBusy ? 'Generando...' : 'Regenerar idiomas'}
                </Button>
              </Stack>
            </Stack>

            <RulesEditor
              title="Español"
              value={config.ruleset?.es}
              onChange={(path, value) => updateRuleset('es', path, value)}
              onAddRule={() => addAirbnbRule('es')}
              onRemoveRule={(index) => removeAirbnbRule('es', index)}
            />

            <Divider />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: '14px', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
                  <CardContent>
                    <Stack spacing={1}>
                      <Typography sx={{ fontSize: 15, fontWeight: 900, color: 'text.primary' }}>English</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>{config.ruleset?.en?.title || 'Pendiente de generar'}</Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{Array.isArray(config.ruleset?.en?.rules) ? `${config.ruleset.en.rules.length} reglas` : 'Sin reglas'}</Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: '14px', boxShadow: 0, border: '1px solid', borderColor: 'divider', opacity: config.airbnb_config?.languages?.includes('pt') ? 1 : 0.7 }}>
                  <CardContent>
                    <Stack spacing={1}>
                      <Typography sx={{ fontSize: 15, fontWeight: 900, color: 'text.primary' }}>Português</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>
                        {config.airbnb_config?.languages?.includes('pt') ? (config.ruleset?.pt?.title || 'Pendiente de generar') : 'Deshabilitado'}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {config.airbnb_config?.languages?.includes('pt')
                          ? (Array.isArray(config.ruleset?.pt?.rules) ? `${config.ruleset.pt.rules.length} reglas` : 'Sin reglas')
                          : 'Actívalo para generar y usar este idioma'}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );

  const visitSettingsContent = config && (
    <Card sx={{ borderRadius: '16px', boxShadow: 0, border: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}>
      <CardContent sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 900, color: 'text.primary' }}>Configuración de visitas</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', mt: 0.5 }}>
              Administra la operación de accesos, QR y reglas por defecto para la validez de visitas.
            </Typography>
          </Box>

          <Stack spacing={1}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary' }}>Tipos de acceso permitidos</Typography>
            <FormControlLabel control={<Switch checked={Boolean(config.rules?.default?.walking)} onChange={(event) => updateConfig('rules.default.walking', event.target.checked)} color="secondary" />} label="Permitir acceso peatonal" />
            <FormControlLabel control={<Switch checked={Boolean(config.rules?.default?.vehicle)} onChange={(event) => updateConfig('rules.default.vehicle', event.target.checked)} color="secondary" />} label="Permitir acceso vehicular" />
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary' }}>Configuración QR</Typography>
            <FormControlLabel control={<Switch checked={Boolean(config.qr_confs?.qr_on_walking)} onChange={(event) => updateConfig('qr_confs.qr_on_walking', event.target.checked)} color="secondary" />} label="QR para acceso peatonal" />
            <FormControlLabel control={<Switch checked={Boolean(config.qr_confs?.qr_on_vehicle)} onChange={(event) => updateConfig('qr_confs.qr_on_vehicle', event.target.checked)} color="secondary" />} label="QR para acceso vehicular" />
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary' }}>Reglas de Check-in</Typography>
            <FormControlLabel control={<Switch checked={Boolean(config.checkin_rules?.require_id_image)} onChange={(event) => updateConfig('checkin_rules.require_id_image', event.target.checked)} color="secondary" />} label="Requerir imagen de identificación" />
            <FormControlLabel control={<Switch checked={Boolean(config.force_resident_vehicle_plate_input)} onChange={(event) => updateConfig('force_resident_vehicle_plate_input', event.target.checked)} color="secondary" />} label="Solicitar placa de vehículo de residente" />
          </Stack>

          <Divider />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Validez por defecto (horas)"
                type="number"
                value={config.rules?.default?.default_lifespan_hours ?? 24}
                onChange={(event) => {
                  const nextValue = Math.max(1, Number(event.target.value) || 1);
                  updateConfig('rules.default.default_lifespan_hours', nextValue);
                }}
                fullWidth
                inputProps={{ min: 1, step: 1 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Validez máxima (días)"
                type="number"
                value={Math.max(1, Math.round((config.rules?.default?.max_lifespan_hours || 24) / 24))}
                onChange={(event) => {
                  const nextDays = Math.max(1, Number(event.target.value) || 1);
                  updateConfig('rules.default.max_lifespan_hours', nextDays * 24);
                }}
                fullWidth
                inputProps={{ min: 1, step: 1 }}
              />
            </Grid>
          </Grid>

          <Stack spacing={1}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary' }}>Opciones de duración</Typography>
            <FormControlLabel control={<Switch checked={Boolean(config.rules?.default?.allow_longer_lifespan)} onChange={(event) => updateConfig('rules.default.allow_longer_lifespan', event.target.checked)} color="secondary" />} label="Permitir ampliar la validez de la visita" />
            <FormControlLabel control={<Switch checked={Boolean(config.rules?.default?.allow_multiple_entries)} onChange={(event) => updateConfig('rules.default.allow_multiple_entries', event.target.checked)} color="secondary" />} label="Permitir múltiples entradas" />
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="sm" disableGutters>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 12 }} data-tour="admin-dashboard-screen">
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            px: 3,
            pt: 3,
            pb: 2,
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(10px)',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton onClick={settingsScreen === 'airbnb' ? handleCloseAirbnbSettings : settingsScreen === 'visits' ? handleCloseVisitSettings : () => navigate(-1)} sx={{ ml: -1, color: 'text.primary' }}>
                <ArrowBackIcon />
              </IconButton>
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 900, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
                  {settingsScreen ? 'Módulos' : 'Configuración administrativa'}
                </Typography>
                <Typography sx={{ fontSize: 24, fontWeight: 900, color: 'text.primary', letterSpacing: '-0.02em' }}>
                  {settingsScreen === 'airbnb' ? 'Configuración Airbnb' : settingsScreen === 'visits' ? 'Configuración de visitas' : 'Dashboard'}
                </Typography>
              </Box>
            </Stack>
            {planChipLabel && <Chip color="secondary" label={planChipLabel} />}
          </Stack>
        </Box>

        <Box sx={{ px: 3, pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {loading && (
            <Card>
              <CardContent sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress color="secondary" />
                <Typography sx={{ color: 'text.secondary', fontWeight: 700 }}>Cargando configuración...</Typography>
              </CardContent>
            </Card>
          )}

          {!loading && error && !config && <Alert severity="error">{error}</Alert>}

          {!loading && config && (
            <>
              {settingsScreen === 'airbnb' ? (
                airbnbSettingsContent
              ) : settingsScreen === 'visits' ? (
                visitSettingsContent
              ) : (
                <>
                  <Card sx={{ borderRadius: '14px', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
                    <CardContent sx={{ p: 1 }}>
                      <Tabs
                        value={activeTab}
                        onChange={(_, nextValue) => setActiveTab(nextValue)}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                      >
                        {TABS.map((tab) => (
                          <Tab key={tab.label} icon={tab.icon} iconPosition="start" label={tab.label} sx={{ textTransform: 'none', fontWeight: 800 }} />
                        ))}
                      </Tabs>
                    </CardContent>
                  </Card>

                  <Card sx={{ borderRadius: '16px', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
                    <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {activeTab === 0 && (
                        <Stack spacing={3}>
                          <FormControl fullWidth>
                            <InputLabel id="notification-mode-label">Notificaciones de Reservaciones</InputLabel>
                            <Select labelId="notification-mode-label" label="Notificaciones de Reservaciones" value={config.notification_mode || 'mail'} onChange={(event) => updateConfig('notification_mode', event.target.value)}>
                              {NOTIFICATION_MODES.map((item) => (
                                <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Divider />
                          <MailingListField label="Emails de Reportería" list={config.reporting_email_list || []} onAdd={(value) => addToList('reporting_email_list', value)} onRemove={(index) => removeFromList('reporting_email_list', index)} />
                          <MailingListField label="Lista de Correo de Averías" list={config.issues_mailing_list || []} onAdd={(value) => addToList('issues_mailing_list', value)} onRemove={(index) => removeFromList('issues_mailing_list', index)} />
                          <MailingListField label="Lista de Correo de Reservaciones" list={config.reservation_mailing_list || []} onAdd={(value) => addToList('reservation_mailing_list', value)} onRemove={(index) => removeFromList('reservation_mailing_list', index)} />
                        </Stack>
                      )}

                      {activeTab === 1 && (
                        <Stack spacing={3}>
                          <Grid container spacing={2}>
                            {ALL_MODULES.map((module) => {
                              const isActive = (config.modules || []).includes(module.key);
                              return (
                                <Grid item xs={12} sm={6} key={module.key}>
                                  <Card
                                    onClick={() => {
                                      const currentModules = Array.isArray(config.modules) ? config.modules : [];
                                      const nextModules = isActive ? currentModules.filter((item) => item !== module.key) : [...currentModules, module.key];
                                      updateConfig('modules', nextModules);
                                    }}
                                    sx={{
                                      borderRadius: '14px',
                                      boxShadow: 0,
                                      border: '1px solid',
                                      borderColor: isActive ? 'secondary.main' : 'divider',
                                      backgroundColor: isActive ? (theme) => alpha(theme.palette.secondary.main, theme.palette.mode === 'dark' ? 0.18 : 0.08) : 'background.paper',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1 }}>
                                        <Typography sx={{ fontSize: 28 }}>{module.icon}</Typography>
                                        <Box sx={{ flex: 1 }}>
                                          <Typography sx={{ fontSize: 14, fontWeight: 900, color: 'text.primary' }}>{module.label}</Typography>
                                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>{isActive ? 'Activo' : 'Inactivo'}</Typography>
                                          {module.key === 'airbnb' && (
                                            <Button
                                              size="small"
                                              color="secondary"
                                              sx={{ px: 0, mt: 0.5, minWidth: 0, textTransform: 'none', fontWeight: 800 }}
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                handleOpenAirbnbSettings();
                                              }}
                                            >
                                              Configurar
                                            </Button>
                                          )}
                                          {module.key === 'visits' && (
                                            <Button
                                              size="small"
                                              color="secondary"
                                              sx={{ px: 0, mt: 0.5, minWidth: 0, textTransform: 'none', fontWeight: 800 }}
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                handleOpenVisitSettings();
                                              }}
                                            >
                                              Configurar
                                            </Button>
                                          )}
                                          {module.key === 'amenities' && (
                                            <Button
                                              size="small"
                                              color="secondary"
                                              sx={{ px: 0, mt: 0.5, minWidth: 0, textTransform: 'none', fontWeight: 800 }}
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                navigate('/amenityadmin');
                                              }}
                                            >
                                              Configurar
                                            </Button>
                                          )}
                                        </Box>
                                      </Stack>
                                      <Switch checked={isActive} color="secondary" />
                                    </CardContent>
                                  </Card>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Stack>
                      )}

                      {activeTab === 2 && (
                        <Stack spacing={3}>
                          <TextField label="Prefijo de Ticket de Avería" value={config.issue_prefix || ''} onChange={(event) => updateConfig('issue_prefix', event.target.value)} inputProps={{ maxLength: 5 }} sx={{ maxWidth: 240 }} />
                          <Stack direction="row" spacing={2} alignItems="center">
                            <TextField label="Color de la Organización" value={config.color || ''} onChange={(event) => updateConfig('color', event.target.value)} sx={{ flex: 1 }} />
                            <Box component="input" type="color" value={config.color || '#238048'} onChange={(event) => updateConfig('color', event.target.value)} sx={{ width: 56, height: 56, border: 'none', background: 'transparent', p: 0, cursor: 'pointer' }} />
                          </Stack>
                          <FormControl fullWidth>
                            <InputLabel id="unit-type-label">Tipo de Unidad</InputLabel>
                            <Select
                              labelId="unit-type-label"
                              label="Tipo de Unidad"
                              value={config.type || ''}
                              onChange={(event) => updateConfig('type', event.target.value)}
                            >
                              {UNIT_TYPES.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                  {type.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}

              {error && !config && <Alert severity="error">{error}</Alert>}
            </>
          )}
        </Box>

        <CustomSnackbar
          open={snackbarOpen}
          message={snackbarMessage}
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        />
      </Box>
    </Container>
  );
}
